import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import NavBar from './nav_bar.js';
import BackButton from './BackButton.js';
import usePageSeo from './seo.js';
import useBfcacheReset from './bfcache.js';
import Lightbox from './lightbox.js';
import SupportLine from './support_line.js';
import './index.css';

const MEDIA_DESC =
  'Buy JUUGs — NFC cards with your cover art on them that you write your MP3JUUG '
  + 'links onto. Sold in packs from $5 for 5. Ships in the US or free local pickup in Atlanta.';

function priceNumber(display) {
  const m = typeof display === 'string' ? display.match(/[\d.]+/) : null;
  return m ? Number(m[0]) : null;
}

// /media — buy JUUGs (blank NFC cards) in pricing tiers. Ships in the US or
// free local pickup in Atlanta. A tier list + a ship/pickup choice, then
// straight to Stripe Checkout. No digital delivery — the artist writes
// links onto the cards once they arrive.
function MediaPage() {
  const [params] = useSearchParams();
  const sessionId = params.get('session_id');

  const [tiers, setTiers] = useState([]);
  const [pickupNote, setPickupNote] = useState('');
  const [posters, setPosters] = useState([]);
  const [failedPosters, setFailedPosters] = useState(() => new Set());
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const [configured, setConfigured] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [tierId, setTierId] = useState('');
  const [fulfillment, setFulfillment] = useState('ship');
  const [starting, setStarting] = useState(false);

  const [order, setOrder] = useState(null);
  const [finalizing, setFinalizing] = useState(!!sessionId);

  // Show the "first pack free" banner only when that offer is switched on.
  const [freeOffer, setFreeOffer] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch('/free-juugs/status');
        const data = await res.json();
        if (!cancelled) setFreeOffer(data);
      } catch { /* banner just stays hidden */ }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch('/media/info');
        const data = await res.json();
        if (cancelled) return;
        setTiers(data.tiers || []);
        setPickupNote(data.pickupNote || '');
        setPosters(
          Array.isArray(data.posters) && data.posters.length
            ? data.posters
            : (data.poster ? [data.poster] : []),
        );
        setConfigured(data.configured !== false);
        if ((data.tiers || []).length) setTierId(data.tiers[0].id);
      } catch {
        if (!cancelled) setError('Could not load the store.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Poll the finalize route after Stripe sends the buyer back.
  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    let tries = 0;
    const tick = async () => {
      tries += 1;
      try {
        const res = await fetch(`/media/session/${encodeURIComponent(sessionId)}`);
        if (res.status === 202) {
          if (!cancelled && tries < 12) setTimeout(tick, 1500);
          else if (!cancelled) { setFinalizing(false); setError('Still processing — check your email in a minute.'); }
          return;
        }
        const data = await res.json();
        if (cancelled) return;
        if (!res.ok) throw new Error(data?.msg || 'Could not load your order.');
        setOrder(data);
        setFinalizing(false);
      } catch (err) {
        if (cancelled) return;
        if (tries < 12) setTimeout(tick, 1500);
        else { setFinalizing(false); setError(err.message); }
      }
    };
    tick();
    return () => { cancelled = true; };
  }, [sessionId]);

  // Back button after a redirect to Stripe restores this page frozen — clear
  // the stuck "Opening checkout…" state.
  useBfcacheReset(() => setStarting(false), starting);

  const buy = useCallback(async () => {
    setStarting(true);
    setError('');
    try {
      const res = await fetch('/media/checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tier: tierId, fulfillment }),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok || !data?.url) throw new Error(data?.msg || 'Could not start checkout.');
      window.location.href = data.url;
    } catch (err) {
      setError(err.message);
      setStarting(false);
    }
  }, [tierId, fulfillment]);

  const shownPosters = posters.filter((src) => !failedPosters.has(src));
  const poster = shownPosters[0] || posters[0] || '';
  const selectedTier = tiers.find((t) => t.id === tierId) || null;
  const shipCents = selectedTier && selectedTier.shipCents > 0 ? selectedTier.shipCents : 0;

  const jsonLd = useMemo(() => {
    if (!tiers.length) return null;
    const offers = tiers.map((t) => {
      const price = priceNumber(t.priceDisplay);
      return {
        '@type': 'Offer',
        name: t.name,
        availability: 'https://schema.org/InStock',
        priceCurrency: 'USD',
        ...(price != null ? { price } : {}),
      };
    });
    return {
      '@context': 'https://schema.org',
      '@type': 'Product',
      name: 'JUUG — NFC music card',
      description: MEDIA_DESC,
      ...(poster ? { image: poster } : {}),
      brand: { '@type': 'Brand', name: 'MP3JUUG' },
      offers,
    };
  }, [tiers, poster]);

  usePageSeo({ title: 'Cop JUUGs — NFC music cards | MP3JUUG', description: MEDIA_DESC, jsonLd });

  if (finalizing) {
    return (
      <>
        <NavBar />
        <main className='page'>
          <div className='page-content'>
            <BackButton fallback='/' />
            <p className='help-text'>Confirming your order…</p>
          </div>
        </main>
      </>
    );
  }

  if (order) {
    return (
      <>
        <NavBar />
        <main className='page'>
          <div className='page-content'>
            <BackButton fallback='/' />
            <div className='card'>
              <h1>Order confirmed — {order.tierName}</h1>
              <p>
                The songs live on the JUUG. Tap it to your phone once it arrives
                and the links open — that's how you'll listen. We apply the cover
                art. Nothing is sent digitally before then.
              </p>
              <p className='help-text'>
                {order.fulfillment === 'pickup'
                  ? (order.pickupNote || pickupNote)
                  : 'Shipping to the address you gave at checkout.'}
              </p>
              {order.email && (
                <p className='help-text'>A copy of this is in your email ({order.email}).</p>
              )}
              <SupportLine>Something off with your order?</SupportLine>
            </div>
          </div>
        </main>
      </>
    );
  }

  return (
    <>
      <NavBar />
      <main className='page'>
        <div className='page-content'>
          <BackButton fallback='/' />
          <h1>Cop JUUGs</h1>
          {freeOffer?.configured && !freeOffer?.claimed && (
            <div className='notice' role='note'>
              <span aria-hidden='true'>🎁</span>
              <span>
                New here? Your first pack of {freeOffer.count || 5} JUUGs is free.{' '}
                <Link to='/free-juugs'>Claim it</Link>.
              </span>
            </div>
          )}
          {shownPosters.length > 0 && (
            <div className='media-gallery' aria-label='JUUG photos'>
              {shownPosters.map((src, i) => (
                <button
                  key={src}
                  type='button'
                  className='media-shot-btn'
                  onClick={() => setLightboxIndex(i)}
                  aria-label={`View photo ${i + 1} of ${shownPosters.length} full size`}
                >
                  <img
                    className='media-shot'
                    src={src}
                    alt={i === 0 ? 'MP3JUUG NFC cards' : 'A JUUG with cover art'}
                    loading='lazy'
                    onError={() => setFailedPosters((f) => new Set(f).add(src))}
                  />
                </button>
              ))}
            </div>
          )}
          <p className='lead'>
            JUUGs are NFC cards you write your links onto and we apply cover art.
            Buy a pack sized to your release — they ship to you, or free local
            pickup in Atlanta. Nothing is delivered digitally.
          </p>

          {loading ? (
            <p className='help-text'>Loading…</p>
          ) : !configured ? (
            <p className='help-text'>The store isn't open yet.</p>
          ) : error && tiers.length === 0 ? (
            <p className='error-text'>{error}</p>
          ) : (
            <div className='card'>
              <div className='field'>
                <span className='label'>Pick one</span>
                {tiers.map((t) => {
                  const img = t.image || poster;
                  return (
                    <label key={t.id} className='radio-line tier-option'>
                      <input
                        type='radio'
                        name='tier'
                        value={t.id}
                        checked={tierId === t.id}
                        onChange={() => setTierId(t.id)}
                      />
                      {img && <img className='tier-thumb' src={img} alt='' loading='lazy' />}
                      <span>
                        <strong>{t.name}</strong>
                        {t.priceDisplay ? ` — ${t.priceDisplay}` : ''}
                        {t.blurb ? <><br /><span className='help-text'>{t.blurb}</span></> : null}
                      </span>
                    </label>
                  );
                })}
              </div>

              <div className='field'>
                <span className='label'>Get it by</span>
                <label className='radio-line'>
                  <input
                    type='radio'
                    name='fulfillment'
                    value='ship'
                    checked={fulfillment === 'ship'}
                    onChange={() => setFulfillment('ship')}
                  />
                  <span>
                    Ship it to me (US)
                    {shipCents > 0 ? ` — +$${(shipCents / 100).toFixed(2)}` : ''}
                    <br />
                    <span className='help-text'>Flat USPS Ground Advantage rate by pack size, added at checkout.</span>
                  </span>
                </label>
                <label className='radio-line'>
                  <input
                    type='radio'
                    name='fulfillment'
                    value='pickup'
                    checked={fulfillment === 'pickup'}
                    onChange={() => setFulfillment('pickup')}
                  />
                  <span>Local pickup{pickupNote ? <><br /><span className='help-text'>{pickupNote}</span></> : null}</span>
                </label>
              </div>

              <button
                type='button'
                className='btn btn-primary'
                onClick={buy}
                disabled={starting || !tierId}
              >
                {starting ? 'Opening checkout…' : 'Buy'}
              </button>
              {error && <p className='error-text'>{error}</p>}
              <SupportLine />
            </div>
          )}
        </div>
      </main>
      <Lightbox
        images={shownPosters.map((src, i) => ({
          src,
          alt: i === 0 ? 'MP3JUUG NFC cards' : 'A JUUG with cover art',
        }))}
        index={lightboxIndex}
        onIndex={setLightboxIndex}
        onClose={() => setLightboxIndex(null)}
      />
    </>
  );
}

export default MediaPage;
